import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { environment } from 'src/environments/environment';
import { DashboardWidgetContextService } from '../services/dashboard-widget-context.service';

/** Paths on `projectsUrl` that dashboard widgets call via {@link DashboadService} and related code. */
const DASHBOARD_WIDGET_PATH_MARKERS = ['/dashboard/', '/widget/', '/attendance/', '/department/'] as const;

/** Own-widget list APIs — omit view-as userID */
const INTERCEPT_EXCLUDED_PATH_PARTS = [
  '/widget/list-widget-dropdown',
  '/widget/my-widgets',
  '/widget/all-widgets'
] as const;

/** Still receive view-as `userID`, but never `teamID` (any load). */
const INTERCEPT_TEAM_ID_EXCLUDED_PATH_PARTS = [
  '/dashboard/my-approvals'
] as const;

/** POST dashboard APIs that receive view-as `userID` as a query param only (body unchanged). */
const INTERCEPT_POST_QUERY_PATH_PARTS = ['/dashboard/setup-projects-overview'] as const;

function isEligibleDashboardWidgetRequest(method: string, pathOnly: string): boolean {
  if (method === 'GET') {
    return true;
  }
  if (method === 'POST') {
    return INTERCEPT_POST_QUERY_PATH_PARTS.some((p) => pathOnly.includes(p));
  }
  return false;
}

/**
 * Appends `userID` and/or `teamID` query params to eligible dashboard widget requests when single-dashboard scope is active.
 */
export const dashboardWidgetContextInterceptor: HttpInterceptorFn = (req, next) => {
  const ctx = inject(DashboardWidgetContextService);
  const scopedUserId = ctx.scopedDashboardUserId();
  const scopedTeamId = ctx.scopedDashboardTeamId();
  if (!scopedUserId && !scopedTeamId) {
    return next(req);
  }

  const base = environment.projectsUrl;
  if (!req.url.startsWith(base)) {
    return next(req);
  }

  const afterBase = req.url.slice(base.length);
  const pathOnly = afterBase.split('?')[0] ?? '';

  if (!isEligibleDashboardWidgetRequest(req.method, pathOnly)) {
    return next(req);
  }

  const matchesWidgetApi = DASHBOARD_WIDGET_PATH_MARKERS.some((m) => pathOnly.includes(m));
  if (!matchesWidgetApi) {
    return next(req);
  }

  if (INTERCEPT_EXCLUDED_PATH_PARTS.some((p) => pathOnly.includes(p))) {
    return next(req);
  }
   const omitTeamId = INTERCEPT_TEAM_ID_EXCLUDED_PATH_PARTS.some((p) => pathOnly.includes(p));

  let params = req.params;
  if (scopedUserId && !params.has('userID')) {
    params = params.set('userID', scopedUserId);
  }
 if (!omitTeamId && scopedTeamId && !params.has('teamID')) {
    params = params.set('teamID', scopedTeamId);
  }
  return next(req.clone({ params }));
};
